import i18next from 'i18next'
import { formatDateInZone } from '@/domains/shared/utils/formatDateTime'
import { useAuthStore } from '@/store/useAuthStore'

/**
 * The structured half of an API refusal. The envelope carries
 * `details` next to `code` and `error` for the few codes whose message alone
 * sent an operator after the wrong thing - a coexistence refusal names the
 * channel and the campaign in the way here. Shapes are per code; each has a
 * reader below that returns nothing for anything else, so a payload the web
 * does not recognise degrades to the plain translation.
 */

export interface CoexistenceConflictDetail {
  channelId: string
  channelName: string
  campaignId: string
  campaignName: string
  startDate: string
  endDate: string
  kind: 'manual' | 'undercut' | 'floor'
  otherKind: 'manual' | 'undercut' | 'floor'
  /** Products both campaigns target on the channel; absent on details recorded before it. */
  overlapCount?: number
  /** The first few of them. */
  overlapSkus?: string[]
}

function isConflict(value: unknown): value is CoexistenceConflictDetail {
  if (typeof value !== 'object' || value === null) return false
  const c = value as Record<string, unknown>
  return (
    typeof c.channelName === 'string' &&
    typeof c.campaignId === 'string' &&
    typeof c.campaignName === 'string' &&
    typeof c.startDate === 'string' &&
    typeof c.endDate === 'string' &&
    typeof c.otherKind === 'string'
  )
}

export function readCoexistenceConflicts(details: unknown): CoexistenceConflictDetail[] {
  if (typeof details !== 'object' || details === null) return []
  const conflicts = (details as { conflicts?: unknown }).conflicts
  return Array.isArray(conflicts) ? conflicts.filter(isConflict) : []
}

/**
 * One line per conflict, as a template: the campaign name sits between
 * `<campaign>` tags so a rich renderer can turn it into a link, and the plain
 * renderer strips them. One translation key serves both.
 */
export function describeCoexistenceConflict(conflict: CoexistenceConflictDetail): string {
  // Outside React (the toast funnel), so the store is read directly - the
  // same zone every campaign date in the app is shown in.
  const timeZone = useAuthStore.getState().timezone
  const period = `${formatDateInZone(conflict.startDate, timeZone)} - ${formatDateInZone(conflict.endDate, timeZone)}`
  const values = {
    channel: conflict.channelName,
    kind: i18next.t(`campaigns.armKind.${conflict.otherKind}`),
    campaign: conflict.campaignName,
    period,
  }
  // Details recorded under the older one-campaign-per-channel rule carry no
  // overlap; they keep the older sentence.
  if (conflict.overlapCount === undefined || conflict.overlapSkus === undefined) {
    return i18next.t('errors.CAMPAIGN_COEXISTENCE_CONFLICT_LINE_CHANNEL', values)
  }
  return i18next.t('errors.CAMPAIGN_COEXISTENCE_CONFLICT_LINE', {
    ...values,
    count: conflict.overlapCount,
    skus: conflict.overlapSkus.join(', '),
  })
}

function stripCampaignTags(line: string): string {
  return line.replace(/<\/?campaign>/g, '')
}

/**
 * Interpolation values for `errors.<CODE>_DETAILED`, or null when the details
 * do not carry what the code's template needs.
 */
export function detailValues(code: string, details: unknown): Record<string, string> | null {
  if (code === 'CAMPAIGN_COEXISTENCE_CONFLICT') {
    const conflicts = readCoexistenceConflicts(details)
    if (conflicts.length === 0) return null
    return {
      conflicts: conflicts.map((c) => stripCampaignTags(describeCoexistenceConflict(c))).join(' '),
      rule: i18next.t('errors.CAMPAIGN_COEXISTENCE_CONFLICT_RULE'),
    }
  }
  return null
}
